<template>
    <div class="rls" aria-hidden="true">
        <!-- Only the charm and rich boards crown a top three. -->
        <div v-if="podium" class="rls-podium">
            <div v-for="n in 3" :key="n" class="rls-place" :class="{ 'is-first': n === 2 }">
                <span class="sk sk-circle rls-avatar"></span>
                <span class="sk rls-name"></span>
                <span class="sk rls-val"></span>
            </div>
        </div>

        <div v-for="n in rows" :key="`r${n}`" class="rls-row">
            <span class="sk rls-no"></span>
            <span class="sk sk-circle rls-pic"></span>
            <span class="rls-text">
                <span class="sk"></span>
                <span class="sk"></span>
            </span>
            <span class="sk rls-amount"></span>
        </div>
    </div>
</template>

<script>
/* The loading state for every ranking board.
 *
 * All of them - charm, rich, room, call, the game boards - render the same
 * row: position, photo, name over level, amount. So they share one skeleton
 * rather than each showing the full-screen dot overlay, which covered the
 * tabs the reader had just tapped.
 *
 * Doubles as the `loadingComponent` for the async tab chunks, which is why
 * both props have defaults.
 */
import '@/assets/css/skeleton.css';

export default {
    name: 'RankListSkeleton',

    props: {
        // Charm and rich boards open on a top-three podium; the rest do not.
        podium: { type: Boolean, default: false },
        rows: { type: Number, default: 8 },
    },
};
</script>

<style scoped>
.rls {
    /* Ink with alpha rather than the default greys: each board sits on its own
       wash - charm pink, rich blue - and a fixed grey disappears into some of
       them. Alpha darkens whatever is behind it, so one value reads on all. */
    --sk-a: rgba(19, 27, 35, 0.085);
    --sk-b: rgba(19, 27, 35, 0.035);

    padding: 4px 10px 10px;
}

/* ---------- Podium ---------- */

.rls-podium {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    align-items: end;
    padding: 14px 4px 18px;
}

.rls-place {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.rls-avatar {
    width: 62px;
    height: 62px;
}

/* The winner sits higher and larger, the way the real podium does. */
.rls-place.is-first {
    padding-bottom: 18px;
}

.rls-place.is-first .rls-avatar {
    width: 76px;
    height: 76px;
}

.rls-name {
    width: 72%;
    height: 11px;
}

.rls-val {
    width: 52%;
    height: 10px;
}

/* ---------- Rows ---------- */

.rls-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 6px;
    border-bottom: 1px solid rgba(19, 27, 35, 0.06);
}

.rls-row:last-child {
    border-bottom: 0;
}

.rls-no {
    flex: 0 0 14px;
    height: 13px;
}

.rls-pic {
    flex: 0 0 42px;
    height: 42px;
}

.rls-text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.rls-text .sk:first-child {
    width: 58%;
    height: 12px;
}

.rls-text .sk:last-child {
    width: 34%;
    height: 10px;
}

.rls-amount {
    flex: 0 0 58px;
    height: 13px;
}
</style>
